import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Form, InputGroup, OverlayTrigger, Placeholder, Tooltip } from "react-bootstrap";

import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { MultiFilterDropdownProps, tagsDropdown } from "./MultiFilterDropdown.d";
import "./MultiFilterDropdown.scss";

const _prepareTags = (tags: string[] | tagsDropdown[]): tagsDropdown[] => {
  return tags.map((tag: string | tagsDropdown) => {
    if (typeof tag === "string") {
      return {
        display: tag,
        value: tag,
      };
    }
    return tag;
  });
};

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const MultiFilterDropdown = ({
  buttonWidth = "150px",
  defaultValue,
  enableSelectAll,
  initialStateText,
  inputLabel,
  inputPlaceholder,
  inputTitle,
  mock = false,
  onSelectTags,
  showSearchBar,
  tags,
  considerEmptyOnAllSelected = false,
}: MultiFilterDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<(string | number)[]>(defaultValue || []);
  const [selectedTagsText, setSelectedTagsText] = useState<string>("");
  const [isAllSelected, setIsAllSelected] = useState(false);

  const { language } = useSession();

  initialStateText = initialStateText || translate(language, "COMPONENTS.MULTI_FILTER_DROPDOWN.ALL");
  inputLabel = inputLabel || translate(language, "COMPONENTS.MULTI_FILTER_DROPDOWN.INPUT_LABEL");
  inputPlaceholder = inputPlaceholder || translate(language, "COMPONENTS.MULTI_FILTER_DROPDOWN.INPUT_PLACEHOLDER");

  const preparedTags = useMemo(() => _prepareTags(tags), [tags]);
  const allValues = useMemo(() => preparedTags.map((t) => t.value), [preparedTags]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    if (defaultValue) setSelectedTags(defaultValue);
  }, [defaultValue]);

  const handleTagCheckboxChange = (tag: tagsDropdown) => {
    if (tag.value === SELECT_ALL_VALUE) {
      const isCurrentlyAll = selectedTags.length === allValues.length || isAllSelected;

      if (isCurrentlyAll) {
        setSelectedTags([]);
        setIsAllSelected(false);
        onSelectTags([]);
      } else {
        setSelectedTags(allValues);
        setIsAllSelected(true);

        if (considerEmptyOnAllSelected) {
          onSelectTags([]);
        } else {
          onSelectTags(allValues);
        }
      }
      return;
    }

    setSelectedTags((prevTags) => {
      let base = prevTags;

      if (isAllSelected) {
        base = allValues;
        setIsAllSelected(false);
      }

      const updatedTags = base.includes(tag.value) ? base.filter((t) => t !== tag.value) : [...base, tag.value];

      if (considerEmptyOnAllSelected && updatedTags.length === allValues.length) {
        setIsAllSelected(true);
        onSelectTags([]);
      } else {
        onSelectTags(updatedTags);
      }

      return updatedTags;
    });
  };

  const filteredTags = useMemo(() => {
    return preparedTags.filter((tag: tagsDropdown) =>
      tag.customSearchFields
        ? tag.customSearchFields.some((attribute) => String(attribute).toLowerCase().includes(searchTerm.toLowerCase()))
        : tag.display.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [preparedTags, searchTerm]);

  const computedSelectedTagsText = useMemo(() => {
    if (isAllSelected) {
      return translate(language, "COMPONENTS.MULTI_FILTER_DROPDOWN.ALL");
    }

    return preparedTags
      .filter((tag) => selectedTags.includes(tag.value))
      .map((tag) => tag.display)
      .join(", ");
  }, [preparedTags, selectedTags, isAllSelected, language]);

  useEffect(() => {
    setSelectedTagsText(computedSelectedTagsText);
  }, [computedSelectedTagsText]);

  const renderSelectAll = () => {
    if (!enableSelectAll) return null;
    return (
      <div
        className="px-3 pb-1 mb-1 border-bottom clickable"
        onClick={() =>
          handleTagCheckboxChange({
            display: "select_all",
            value: SELECT_ALL_VALUE,
          })
        }
      >
        <Form.Check
          className="d-flex justify-content-between flex-row-reverse ps-0 s-regular"
          type="checkbox"
          label={translate(language, "COMPONENTS.MULTI_FILTER_DROPDOWN.SELECT_ALL")}
          checked={isAllSelected || selectedTags.length === allValues.length}
          onChange={() => {}}
        />
      </div>
    );
  };

  const renderTags = () =>
    filteredTags.map((tag, index) => (
      <div key={`${tag.value}-${index}`} onClick={() => handleTagCheckboxChange(tag)}>
        <Form.Check
          className="d-flex justify-content-between flex-row-reverse ps-0 s-regular"
          type="checkbox"
          label={tag.display}
          checked={isAllSelected || selectedTags.includes(tag.value)}
          onChange={() => {}}
        />
      </div>
    ));

  const renderDropdownToggle = () => (
    <Dropdown.Toggle variant="light" className="s-bold text-start d-flex">
      <div>
        <div className="dark-gray-font">{inputTitle}</div>
        <div className="text-truncate" style={{ width: buttonWidth }}>
          {selectedTagsText || <span>{initialStateText}</span>}
        </div>
      </div>
      <div className="align-self-center ms-2">
        <span className="dst-icon-chevron-down" />
      </div>
    </Dropdown.Toggle>
  );

  if (mock) {
    return (
      <Placeholder animation="glow">
        <Placeholder.Button
          style={{
            width: buttonWidth,
            height: 50,
          }}
          className="s-bold"
          aria-hidden="true"
        />
      </Placeholder>
    );
  }

  return (
    <Dropdown className="multi-filter-dropdown">
      {selectedTagsText.length > 0 ? (
        <OverlayTrigger placement="bottom" overlay={<Tooltip id="button-tooltip">{selectedTagsText}</Tooltip>}>
          {renderDropdownToggle()}
        </OverlayTrigger>
      ) : (
        renderDropdownToggle()
      )}
      <Dropdown.Menu style={{ minWidth: "300px" }}>
        {showSearchBar && (
          <Form.Group className="m-3">
            <InputGroup>
              <InputGroup.Text className="s-regular">{inputLabel}</InputGroup.Text>
              <Form.Control
                className="s-regular"
                type="text"
                placeholder={inputPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </InputGroup>
          </Form.Group>
        )}
        {renderSelectAll()}
        <div className="my-2 m-3 scrollable size-dropdown">{renderTags()}</div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MultiFilterDropdown;
